import { useCallback, useEffect } from "react";
import {
	useAlertContext,
	useConsultContext,
	useUserContext
} from "../../contexts";
import { AlertType, Consult } from "../../models";
import { apiAccessRequest, StatusReason, VisitType } from "../../constants";
import { BuildUrl, convertLocalDateToUTCDate, mapConsult } from "../../helpers";
import { useMsal } from "@azure/msal-react";
import { format } from "date-fns/format";

export const GetConsults = () => {
	const { instance } = useMsal();
	const { account } = useUserContext();
	const {
		setConsults,
		setCompletedConsultsCount,
		refreshConsults,
		setRefreshConsults
	} = useConsultContext();
	const { showAlert } = useAlertContext();

	const getConsults = useCallback(async () => {
		try {
			const requestUrl = BuildUrl("Consult/telehealth");
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				}),
				...{ "Content-Type": "application/json" }
			};
			const response = await fetch(requestUrl, {
				method: "GET",
				headers
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			const contentLength = response.headers.get("Content-Length");
			if (response.status !== 204 && contentLength !== "0") {
				const result = await response.json();
				const consults = result
					.filter(
						(consult: Consult) =>
							consult.statusReason !== StatusReason.COMPLETED
					)
					.map((consult: Consult) => mapConsult(consult));
				setConsults(consults);
			}
		} catch (error) {
			console.info(error);
			showAlert({
				message: "Consults could not be retrieved",
				alertType: AlertType.error
			});
			return;
		}
	}, [account, instance, setConsults, showAlert]);

	const getCompletedConsults = useCallback(async () => {
		try {
			const requestUrl = BuildUrl("Consult/completedcount");
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				}),
				...{ "Content-Type": "application/json" }
			};
			const response = await fetch(requestUrl, {
				method: "POST",
				headers,
				body: JSON.stringify({
					visitTypes: [VisitType.RemoteTelehealth],
					startDate: convertLocalDateToUTCDate(
						`${format(Date.now(), "yyyy-MM-dd")} 00:00`
					),
					endDate: convertLocalDateToUTCDate(
						`${format(Date.now(), "yyyy-MM-dd")} 23:59`
					)
				})
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			const contentLength = response.headers.get("Content-Length");
			if (response.status !== 204 && contentLength !== "0") {
				const result = await response.json();
				setCompletedConsultsCount(result.count);
			}
		} catch (error) {
			console.info(error);
			showAlert({
				message: "Consults could not be retrieved",
				alertType: AlertType.error
			});
			return;
		}
	}, [account, instance, setCompletedConsultsCount, showAlert]);

	useEffect(() => {
		getConsults();
		getCompletedConsults();
		return;
	}, [getConsults, getCompletedConsults]);

	useEffect(() => {
		if (refreshConsults) {
			getConsults();
			getCompletedConsults();
			setRefreshConsults(false);
		}
	}, [getCompletedConsults, getConsults, refreshConsults, setRefreshConsults]);

	return <></>;
};
